import { Link } from "react-router";

import { VIEWING_ROOMS_COPY } from "./viewingRooms.constants.ts";
import type { RoomRow } from "./viewingRooms.model.ts";

/**
 * The link, as a field rather than a button alone.
 *
 * Read-only and selectable, so a browser that refuses the clipboard leaves the
 * address somewhere it can be copied by hand — which is the whole reason there
 * is no `document.execCommand` fallback in the controller.
 */
export function RoomLink({
  url,
  copied,
  onCopy,
}: {
  url: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="rooms__link">
      <input className="rooms__link-input" value={url} readOnly aria-label="Room link" />
      <button
        type="button"
        className={`rooms__copy${copied ? " rooms__copy--done" : ""}`}
        onClick={onCopy}
      >
        {copied ? VIEWING_ROOMS_COPY.copied : VIEWING_ROOMS_COPY.copyLink}
      </button>
    </div>
  );
}

export function RoomList({
  rows,
  copiedId,
  confirmingId,
  busy,
  onCopy,
  onHide,
  onAskDelete,
  onDelete,
  onEditRoom,
}: {
  rows: RoomRow[];
  copiedId: string | null;
  confirmingId: string | null;
  busy: boolean;
  onCopy: (roomId: string, url: string) => void;
  onHide: (roomId: string, hidden: boolean) => void;
  onAskDelete: (roomId: string | null) => void;
  onDelete: (roomId: string) => void;
  onEditRoom: (roomId: string) => void;
}) {
  return (
    <ul className="rooms__list">
      {rows.map((row) => (
        <li
          key={row.id}
          className={`rooms__row${row.hidden ? " rooms__row--hidden" : ""}`}
        >
          <div className="rooms__row-main">
            <Link to={row.href} className="rooms__name">
              {row.title}
            </Link>
            <p className="rooms__meta">{row.meta}</p>
            <p className="rooms__shows">{row.shows}</p>

            {row.canCopy ? (
              <RoomLink
                url={row.url}
                copied={copiedId === row.id}
                onCopy={() => onCopy(row.id, row.url)}
              />
            ) : (
              <p className="rooms__note">{VIEWING_ROOMS_COPY.hiddenNote}</p>
            )}
          </div>

          <div className="rooms__row-actions">
            <Link to={row.href} className="rooms__action">
              {VIEWING_ROOMS_COPY.open}
            </Link>
            <button
              type="button"
              className="rooms__action"
              disabled={busy}
              onClick={() => onEditRoom(row.id)}
            >
              {VIEWING_ROOMS_COPY.edit}
            </button>
            <button
              type="button"
              className="rooms__action"
              disabled={busy}
              onClick={() => onHide(row.id, !row.hidden)}
            >
              {row.hidden ? VIEWING_ROOMS_COPY.reopen : VIEWING_ROOMS_COPY.hide}
            </button>

            {confirmingId === row.id ? (
              <>
                <button
                  type="button"
                  className="rooms__danger"
                  disabled={busy}
                  onClick={() => onDelete(row.id)}
                >
                  {VIEWING_ROOMS_COPY.removeConfirmAction}
                </button>
                <button
                  type="button"
                  className="rooms__action"
                  onClick={() => onAskDelete(null)}
                >
                  {VIEWING_ROOMS_COPY.keep}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="rooms__action"
                onClick={() => onAskDelete(row.id)}
              >
                {VIEWING_ROOMS_COPY.remove}
              </button>
            )}
          </div>

          {confirmingId === row.id && (
            <p className="rooms__confirm" role="alert">
              {VIEWING_ROOMS_COPY.removeConfirmDetail}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}
